import React, { useState, useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'

// components
import { useTranslation } from 'react-i18next'

import {
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Button,
  NativeSelect,
} from '@mui/material'
import Loading from '../../components/Loading'
import PageTitle from '../../components/PageTitle'
import { useNotificationsProvider } from '../../context/NotificationsContext'
import { useDataApi } from '../../context/DataApiContext'
import { useUserState } from '../../context/user/UserContext'
import useStyles from './styles'
import { useQueryState } from '../../utils/query-state'

import Table from '../../components/Table'
import ModifySubscriptionDialog from '../../components/Users/components/ModifySubscriptionDialog'
import generateColumns from './columns'
import AutoCompleteInput from '../../components/AutoCompleteInput'
import enums from '../../utils/enums'

function useQuery() {
  const { search } = useLocation()

  return useMemo(() => new URLSearchParams(search), [search])
}

export default function SubscriptionsList({ history }) {
  const { t } = useTranslation()

  const classes = useStyles()
  const { dataProvider } = useDataApi()
  const { showNotification } = useNotificationsProvider()

  const [loading, setLoading] = useState(true)
  const [tableState, setTableState] = useState({
    page: 0,
    sort: null,
    filters: null,
  })
  const [tableData, setTableData] = useState(null)

  const [openModifySubscriptionDialog, setOpenModifySubscriptionDialog] =
    useState(false)
  const [subscriptionToModify, setSubscriptionToModify] = useState(false)

  const { user } = useUserState()

  const query = useQuery()

  const [member, setMember] = useQueryState({
    paramName: 'member',
    history,
    query,
  })
  const [constructor, setConstructor] = useQueryState({
    paramName: 'constructor',
    history,
    query,
  })
  const [status, setStatus] = useQueryState({
    paramName: 'status',
    history,
    query,
    defaultValue: '',
  })

  const computedFilters = useMemo(() => {
    const filtersObj = {
      memberId: member ? member.id : null,
      constructorId: constructor ? constructor.id : null,
      status: status || null,
    }
    // remove nulls
    Object.keys(filtersObj).forEach(
      (key) => filtersObj[key] === null && delete filtersObj[key]
    )
    return filtersObj
  }, [member, constructor, status, query, history])

  const refreshData = (keepPage = false) => {
    if (keepPage) {
      setTableState({ page: tableState.page, sort: null, filters: null })
    } else {
      setTableState({ page: 0, sort: null, filters: null })
    }
  }

  const resource = 'users/subscriptions'

  useEffect(() => {
    dataProvider
      .getList(
        resource,
        {
          range: { page: tableState.page + 1 },
          filters: tableState.filters,
          sort: tableState.sort,
        },
        null,
        false
      )
      .then((response) => {
        setTableData(response.data)
        setLoading(false)
      })
      .catch(() => showNotification('error', t('errors.api-error')))
    // eslint-disable-next-line
  }, [tableState])

  const onModify = (subscription) => {
    setSubscriptionToModify(subscription)
    setOpenModifySubscriptionDialog(true)
  }

  const updateFilters = () => {
    setTableState({
      ...tableState,
      page: 0,
      filters: computedFilters,
      rowsSelected: [],
    })
  }

  const clearFilters = () => {
    setMember(null)
    setConstructor(null)
    setStatus('')
    setTableState({
      ...tableState,
      page: 0,
      filters: null,
      rowsSelected: [],
    })
  }

  const tableColumns = generateColumns({
    t,
    classes,
    tableData,
    user,
    onModify,
  })

  if (loading) {
    return <Loading />
  }
  return (
    <>
      <ModifySubscriptionDialog
        open={openModifySubscriptionDialog}
        onClose={() => setOpenModifySubscriptionDialog(false)}
        onModified={() => {
          setOpenModifySubscriptionDialog(false)
          refreshData(true)
        }}
        title={t('modify-subscription-dialog.title')}
        subscriptionToModify={subscriptionToModify}
      />
      <Grid container alignItems="center" justifyContent="space-between">
        <Grid item xs={12} sm={4}>
          <PageTitle title={t('subscriptions.title')} />
        </Grid>
      </Grid>
      <Grid container item className={classes.filtersContainer}>
        <Grid item>
          <Typography variant="h6" className={classes.filterContainerTitleText}>
            {t('common.filterZone')}
          </Typography>
        </Grid>
        <Grid
          item
          container
          alignItems="center"
          justifyContent="flex-start"
          spacing={1}
        >
          <Grid item xs={12} md={3}>
            <AutoCompleteInput
              onChange={setMember}
              label={t('common.member')}
              resource="members/autocomplete"
              value={member}
              canDelete
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <AutoCompleteInput
              onChange={setConstructor}
              label={t('common.constructeur')}
              resource="constructors/autocomplete"
              value={constructor}
              canDelete
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl variant="standard" className={classes.selector}>
              <InputLabel
                id="status-label"
                shrink={enums.SUBSCRIPTION_STATUS.isValid(status)}
              >
                {t('subscriptions.status')}
              </InputLabel>
              <NativeSelect
                labelid="conformity-label"
                inputProps={{
                  name: t('common.conformity'),
                  id: 'conformity',
                }}
                onChange={(event) => setStatus(event.target.value)}
                value={status}
              >
                <option aria-label="None" value="" />
                <option value={enums.SUBSCRIPTION_STATUS.NOT_SUBSCRIBED}>
                  {t('subscriptions.not-subscribed')}
                </option>
                <option value={enums.SUBSCRIPTION_STATUS.NOT_PAID}>
                  {t('subscriptions.not-paid')}
                </option>
                <option value={enums.SUBSCRIPTION_STATUS.PAID}>
                  {t('subscriptions.paid')}
                </option>
              </NativeSelect>
            </FormControl>
          </Grid>
        </Grid>
        <Grid
          item
          container
          spacing={2}
          justifyContent="flex-end"
          className={classes.actionsContainer}
        >
          <Grid item xs={12} md={2} xl={1}>
            <Button
              color="secondary"
              variant="contained"
              onClick={clearFilters}
              fullWidth
            >
              {t('common.clean')}
            </Button>
          </Grid>
          <Grid item xs={12} md={2} xl={1}>
            <Button
              color="primary"
              variant="contained"
              fullWidth
              onClick={updateFilters}
            >
              {t('common.search')}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid item className={classes.tableContainer}>
        <Table
          id={resource}
          columns={tableColumns}
          data={tableData}
          page={tableState.page}
          sort={tableState.sort}
          onChangePage={(currentPage) => {
            setTableState({ ...tableState, page: currentPage })
          }}
          onColumnSortChange={(changedColumn, direction) => {
            const newSort = {
              field: changedColumn,
              direction: direction.toUpperCase(),
            }
            setTableState({ ...tableState, sort: newSort })
          }}
        />
      </Grid>
    </>
  )
}
